import React, { useCallback, useContext, useEffect, useState } from "react";
import Button from "components/UI-lib/Button";
import FlatCard from "components/UI-lib/FlatCard";
import agent from "server";
import { OrgContext } from "contexts/OrgContext";
import GenericCard from "assets/img/svg/genericcard.svg?react";
import SimpleTransition from "components/SimpleTransition";
import {
  AddressElement,
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import InlineNotification from "contexts/Notifications/InlineNotifications";
import { UserContext } from "contexts/UserContext";
import { FeatureToggle } from "components/DevToggle";
import { createDefaultAnalyticsObject } from "components/Analytics";
import CreditCardDetails from "./CreditCardDetails";
import CalTech from "./CalTech.png";
import instacart from "./instacart.png";
import { DarkModeContext } from "contexts/DarkModeContext";
import { BillingContext } from "contexts/BillingContext";
import {
  STRIPE_CONNECTION_ERROR,
  useStripeClientSecret,
} from "server/brevdeploy/hooks/useStripeClientSecret";
import { BREV_SUPPORT_EMAIL } from "constants/index";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { Button as DialogButton } from "components/Graphs/Button";

export interface ConnectedCard {
  id: string;
  brand: string;
  last4: string;
  expiration: string;
  latestPayment?: any;
}

const ConnectCard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [defaultCard, setDefaultCard] = useState<ConnectedCard | null>(null);
  const [connectedCards, setConnectedCards] = useState<ConnectedCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<string>("");
  const [showCardModal, setShowCardModal] = useState(false);
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("info");
  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const billingContext = useContext(BillingContext);

  const instacartOfferStatus =
    userContext.me?.onboardingData.instacart === "true";
  const stripe = useStripe();
  const elements = useElements();
  const { darkMode } = useContext(DarkModeContext);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [acknowledgedWarning, setAcknowledgedWarning] = useState(false);
  const { clientSecret, clientSecretLoading, clientSecretError } =
    useStripeClientSecret({
      activeOrgId: orgContext.activeOrgId,
      enabled: showCardModal,
    });

  const getPaymentMethods = useCallback(async () => {
    if (!orgContext.activeOrgId) {
      return;
    }

    setLoadingPaymentMethods(true);
    const res = await agent.Billing.getPaymentMethods(orgContext.activeOrgId);
    if (res.success && res.data) {
      setDefaultCard(res.data.defaultCard || null);
      setConnectedCards(res.data.paymetMethods || []);
    }
    setLoadingPaymentMethods(false);
  }, [orgContext.activeOrgId]);

  const handleDeleteCard = (cardId: string) => {
    setCardToDelete(cardId);
    setAcknowledgedWarning(false);
    setDeleteDialogOpen(true);
  };

  const handleSetDefault = async (cardId: string) => {
    if (!orgContext.activeOrgId) {
      return;
    }

    setLoading(true);
    const wasAutoRechargeOn = billingContext.autoRechargeEnabled;
    const res = await agent.Billing.setDefaultPaymentMethod(
      orgContext.activeOrgId,
      cardId,
    );
    if (res.success) {
      setInlineAlertSeverity("success");
      setInlineAlertMessage(
        wasAutoRechargeOn
          ? "Default payment method updated. Auto-recharge is now connected to your new default card."
          : "Default payment method updated",
      );
      await getPaymentMethods();
    } else {
      setInlineAlertSeverity("error");
      setInlineAlertMessage("Failed to update default payment method.");
    }
    setLoading(false);
  };

  const uniqueCardIds = new Set([
    ...(defaultCard ? [defaultCard.id] : []),
    ...connectedCards.map((c) => c.id),
  ]);
  const isLastCard = uniqueCardIds.size === 1;
  const isDeletingDefault =
    cardToDelete !== null && defaultCard?.id === cardToDelete;
  const hasOtherCards = uniqueCardIds.size > 1;

  const confirmDeleteCard = async () => {
    if (!cardToDelete || !orgContext.activeOrgId) return;
    setIsDeleting(true);
    const wasLastCard = isLastCard;
    const wasDeletingDefault = isDeletingDefault;
    const wasHasOtherCards = hasOtherCards;
    const wasAutoRechargeOn = billingContext.autoRechargeEnabled;
    const res = await agent.Billing.deletePaymentMethod(
      orgContext.activeOrgId,
      cardToDelete,
    );
    // Close the dialog before refreshes so the dialog content doesn't flicker mid-update.
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setCardToDelete(null);
    if (res.success) {
      setInlineAlertSeverity("success");
      let message = "Card removed successfully";
      if (wasLastCard && wasAutoRechargeOn) {
        message =
          "Card removed. Auto-recharge has been turned off because no payment method is attached.";
      } else if (wasDeletingDefault && wasHasOtherCards && wasAutoRechargeOn) {
        message =
          "Card removed. Auto-recharge is now connected to your new default card.";
      }
      setInlineAlertMessage(message);
      await getPaymentMethods();
      if (wasLastCard && wasAutoRechargeOn) {
        await billingContext.fetchBillingProfile();
      }
    } else {
      setInlineAlertSeverity("error");
      setInlineAlertMessage("Failed to remove card.");
    }
  };

  useEffect(() => {
    if (orgContext.activeOrgId) {
      // Fetch payment methods when the active organization changes.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void getPaymentMethods();
    }
  }, [getPaymentMethods, orgContext.activeOrgId]);

  const handleSubmitCreditCard = async () => {
    setLoading(true);
    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements) {
      setLoading(false);
      return;
    }
    if (!clientSecret) {
      setInlineAlertSeverity("error");
      setInlineAlertMessage(STRIPE_CONNECTION_ERROR);
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const addressElement = elements.getElement(AddressElement);
    if (!cardElement || !addressElement) {
      setLoading(false);
      return;
    }

    const { complete, value: addressValue } = await addressElement.getValue();
    if (
      addressValue.name === "" ||
      addressValue.address.line1 === ""
      // addressValue.address.city === "" ||
      // addressValue.address.state === "" ||
      // addressValue.address.postal_code === ""
      // addressValue.address.country === ""
    ) {
      setInlineAlertSeverity("error");
      setInlineAlertMessage("Please enter a valid address");
      setLoading(false);
      return;
    }
    const res = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        metadata: {
          userId: userContext.me?.id || "",
          orgId: orgContext.activeOrgId,
        },
        billing_details: {
          address: {
            line1: addressValue.address.line1,
            // line2: value.address.line2,
            city: addressValue.address.city,
            state: addressValue.address.state,
            postal_code: addressValue.address.postal_code,
            country: addressValue.address.country,
          },
        },
      },
    });

    if (res.error) {
      // Show error to your customer (for example, payment details incomplete)
      console.error(res.error.message);
      setInlineAlertSeverity("error");
      setInlineAlertMessage(
        `Unable to submit credit card details. Please try again or contact support at ${BREV_SUPPORT_EMAIL}.`,
      );
    } else {
      cardElement.clear();
      setInlineAlertSeverity("info");
      setInlineAlertMessage("Credit card details saved successfully!");
      setShowCardModal(false);
      await getPaymentMethods();
      //   if (onComplete) await onComplete();
    }
    setLoading(false);
  };

  const acceptCalTechOffer = async () => {
    setLoading(true);
    userContext.setOnboardingObjectFunc({
      caltechOffer: "true",
    });
    const res = await agent.Users.update(
      userContext.me?.id || "",
      createDefaultAnalyticsObject(userContext.me, {
        caltechOffer: "true",
      }),
    );
    if (res.success) {
      setInlineAlertMessage(
        "Congrats! This org has the credits applied through June 16, 2023",
      );
      setInlineAlertSeverity("success");
    } else {
      setInlineAlertMessage(
        "The offer couldn't be redeemed. The team has been alerted",
      );
      setInlineAlertSeverity("error");
    }
    setLoading(false);
  };
  const acceptInstacartOffer = async () => {
    setLoading(true);
    await userContext.setOnboardingObjectFunc({
      instacart: "true",
    });
    const res = await agent.Users.update(
      userContext.me?.id || "",
      createDefaultAnalyticsObject(userContext.me, {
        instacart: "true",
      }),
    );
    if (res.success) {
      setInlineAlertMessage(
        "Congrats! This org has the credits applied through June 01, 2023",
      );
      setInlineAlertSeverity("success");
      await userContext.refreshMe();
    } else {
      setInlineAlertMessage(
        "The offer couldn't be redeemed. The team has been alerted",
      );
      setInlineAlertSeverity("error");
    }
    setLoading(false);
  };

  return (
    <>
      <h1 className="text-lg font-medium dark:text-white">1. Connect Card</h1>
      {billingContext.billingProfile?.billing_type === "usage-arrears" && (
        <p className="mb-2 text-sm text-gray-700 dark:text-secondary">
          Put a card on file to access compute. A $20 authorization charge will
          be placed on your card which will be reverted within 7 days. You will
          be billed in $20 increments for compute usage.{" "}
          <a
            href={`mailto:${BREV_SUPPORT_EMAIL}`}
            className="underline dark:hover:text-slate-300"
          >
            Get in touch with us if you would like to change your billing
            thresholds!
          </a>
        </p>
      )}
      {userContext.me?.onboardingData.caltechOffer === "true" && (
        <p className="text-highlight">
          You applied credits to this org. All users added will have free access
          until June 16, 2023
        </p>
      )}
      {instacartOfferStatus && (
        <p className="text-highlight">
          You applied credits to this org. All users added will have free access
          until June 01, 2023
        </p>
      )}

      <InlineNotification
        show={!!clientSecretError || !!inlineAlertMessage}
        severity={clientSecretError ? "error" : inlineAlertSeverity || "info"}
        text={clientSecretError || inlineAlertMessage}
        autoClose={false}
        hideCloseButton={!!clientSecretError}
        onClose={() => setInlineAlertMessage("")}
      />

      {!instacartOfferStatus && (
        <FeatureToggle feature="instacart">
          <hr className="mt-2" />
          <div className="mt-3 text-sm text-gray-700 dark:text-secondary">
            <span className="flex flex-col items-start justify-start">
              <img src={instacart} alt="Instacart Logo" width={80} />
              <p className="text-highlight">
                $25 per user per month, free until June 01, 2023
              </p>
              <a
                className="mt-2 underline"
                target="_blank"
                rel="noreferrer"
                href="https://brev.dev/terms"
              >
                Terms apply
              </a>
            </span>
            <Button
              label="Accept Offer"
              type="primary"
              className="mt-3"
              onClick={() => {
                acceptInstacartOffer();
              }}
            />
            <hr className="mt-3" />
          </div>
        </FeatureToggle>
      )}
      {userContext.me?.onboardingData.caltechOffer !== "true" && (
        <FeatureToggle feature="BEBi205">
          <hr className="mt-2" />
          <div className="mt-3 text-sm text-gray-700">
            <span className="flex flex-col items-start justify-start">
              <img
                src={CalTech}
                alt="Cal Tech Logo"
                width={80}
                // className="mr-3"
              />
              <p className="text-highlight">
                $25 per user per month, free for Spring Term 2023 (until June
                16th, 2023)
              </p>
              <a
                className="mt-2 underline"
                target="_blank"
                rel="noreferrer"
                href="https://brev.dev/terms"
              >
                Terms apply
              </a>
            </span>
            <Button
              label="Accept Offer"
              type="primary"
              className="mt-3"
              onClick={() => {
                acceptCalTechOffer();
              }}
            />
            <hr className="mt-3" />
          </div>
        </FeatureToggle>
      )}
      <Button
        label={defaultCard ? "Connect a new card" : "Connect a card"}
        type="secondary"
        className="mt-3"
        iconLeft={<GenericCard className="h-5 w-7 mr-3" />}
        onClick={() => {
          setShowCardModal(!showCardModal);
        }}
      />
      <hr className="mt-2 dark:border dark:border-zinc-800" />
      <SimpleTransition show={showCardModal}>
        <div className="mb-10">
          <div className="ph-mask mt-6">
            <h3
              id="payment-heading"
              className="text-lg font-medium text-gray-900 dark:text-white"
            >
              Payment details
            </h3>
            <CardElement
              options={{
                style: {
                  base: {
                    color: darkMode ? "#cffafe" : "black",
                  },
                },
                classes: {
                  base: "rounded-md border-gray-300 dark:border-zinc-800 shadow-sm p-3 sm:text-sm stripe-border",
                  focus: "border-highlight ring-cyan-500",
                },
                hidePostalCode: false,
              }}
            />
            <AddressElement
              className="mt-3"
              options={{
                mode: "billing",
                fields: {},
                display: {},

                // required: true,
                // display: {
                //   name: "full",
                // },
                // name: "address",
              }}
            />

            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse col-span-4">
              <Button
                className="ml-3"
                label="Save"
                type="primary"
                disabled={loading || clientSecretLoading || !clientSecret}
                loading={loading}
                onClick={() => {
                  handleSubmitCreditCard();
                }}
              />
              <Button
                label="Cancel"
                type="secondary"
                onClick={() => {
                  setShowCardModal(false);
                }}
              />
            </div>
          </div>
        </div>
      </SimpleTransition>
      {!showCardModal && (
        <>
          {!loadingPaymentMethods ? (
            <>
              <CreditCardDetails
                connectedCard={defaultCard}
                isDefault
                onDelete={handleDeleteCard}
              />
              {connectedCards.filter((c) => c.id !== defaultCard?.id).length >
                0 && (
                <>
                  <span className="flex text-sm my-2">Other Cards:</span>
                  <div className="flex flex-col">
                    {connectedCards
                      .filter((c) => c.id !== defaultCard?.id)
                      .map((connectedCard) => (
                        <div key={connectedCard.id}>
                          <CreditCardDetails
                            connectedCard={connectedCard}
                            onDelete={handleDeleteCard}
                            onSetDefault={handleSetDefault}
                          />
                        </div>
                      ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-col justify-center items-center w-full">
              <div className="w-full">
                <FlatCard className="" isLoading={true} noBackground>
                  <p className="text-sm text-gray-700 dark:text-secondary">
                    Fetching payment methods
                  </p>
                </FlatCard>
              </div>
            </div>
          )}
        </>
      )}
      <div className="mt-3" />

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setCardToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="pb-2 text-white">
              Remove Payment Method
            </DialogTitle>
            <DialogDescription asChild className="text-gray-300">
              <div>
                <p>Are you sure you want to remove this card?</p>
                {isLastCard && (
                  <div className="mt-2 rounded-md bg-red-900/50 border border-red-500 p-3">
                    <p className="text-red-400 font-semibold">
                      Warning: This is your only payment method.
                    </p>
                    <p className="mt-1 text-red-300 text-sm">
                      Without a payment method, auto-recharge will be disabled.
                      When your credits run out, all running instances will be
                      stopped and any unsaved work will be lost.
                    </p>
                    <label className="mt-3 flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acknowledgedWarning}
                        onChange={(e) =>
                          setAcknowledgedWarning(e.target.checked)
                        }
                        className="h-4 w-4 rounded border-red-500 text-red-500 focus:ring-red-500"
                      />
                      <span className="text-red-300 text-sm">
                        I understand my instances will be deleted
                      </span>
                    </label>
                  </div>
                )}
                {isDeletingDefault && hasOtherCards && connectedCards[0] && (
                  <p className="ph-mask mt-2 text-yellow-400">
                    This is your default payment method. Your{" "}
                    {connectedCards[0].brand} card ending in{" "}
                    {connectedCards[0].last4} will become the new default.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogButton
              variant="secondary"
              onClick={() => {
                setDeleteDialogOpen(false);
                setCardToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </DialogButton>
            <DialogButton
              variant="destructive"
              onClick={confirmDeleteCard}
              disabled={isDeleting || (isLastCard && !acknowledgedWarning)}
              isLoading={isDeleting}
            >
              {isDeleting ? "Removing..." : "Remove Card"}
            </DialogButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectCard;

export const ConnectCardWrapper: React.FC = () => {
  return (
    <div className="">
      <FlatCard noBackground>
        <ConnectCard />
      </FlatCard>
    </div>
  );
};
